import { AssetField } from '@/components/builder/asset-field';
import {
  AlignExtra,
  AlignGrid,
  AngleDial,
  Disclosure,
  Popover,
  Section,
  Slider,
  ToggleGroup,
  TokenSwatchGrid,
  useScrub,
} from '@/components/builder/inspector-controls';
import {
  BoolInput,
  ConfigField,
  Field,
  NumberInput,
  PaddingField,
  RequiresField,
  SelectInput,
  SizeField,
  SpansField,
  TextInput,
  TokenPicker,
} from '@/components/builder/inspector-fields';
import { nodeIssues, sizeOptions, type FieldIssues, type StepCtx } from '@/components/builder/validate';
import { cn } from '@/lib/cn';
import { ColorMintForm } from '@/components/builder/color-mint';
import { ThemeTokenSample } from '@/components/builder/token-navigation';
import { summarizeThemeToken } from '@/lib/theme-token-summary';
import { mintColorToken, mintFontVariant, nodeLabel, type Frame, type AuthoringDoc, type ThemeTokenFamily } from '@personalizehq/editor';
import type { AuthoringBlockNode as BlockNode } from '@personalizehq/schemas';
import type { Action, Align, Gradient, Justify, Shadow, Size, SizeVal, Style, Theme } from '@personalizehq/schemas/ir';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowRight,
  Ban,
  Blend,
  CaseLower,
  CaseSensitive,
  CaseUpper,
  Crop,
  Expand,
  Shrink,
  Square,
  Strikethrough,
  Underline,
  X,
} from 'lucide-react';
import { useState } from 'react';

// THE SECTION REGISTRY (B2).
//
// Plasmic needs `getOrderedSections()` plus ~20 `isTplX()` predicates because its element space is
// OPEN — anyone can register a component, so which panels apply has to be computed. Ours is a CLOSED
// 12-member union, so that whole layer collapses into one exhaustive table that the compiler checks.
//
// Section ORDER varies by block, and that is the point: an `image` leads with its source and its
// media controls, a `stack` leads with layout. Same panels, different first thing you see, which is
// most of what makes a panel feel considered.
//
// R1 — THE RULE THAT KEEPS US HONEST. A section is omitted only when the block TYPE HAS NO SUCH
// FIELD, never because the UI judges it meaningless. `style`, `actions`, `a11y_*`, `requires` and
// `animate` all live on `BlockBase`, so EVERY block gets style/motion/interaction/compat — including
// `spacer`, which the plan's own sketch wanted to cut down to "size, style, compat". A spacer may
// legally carry an action and a background; hiding those would invent a law the schema does not have.
// (Plasmic hides `background` on an image for taste. That is exactly the mistake.)
/** The three values that name a PLACE, in screen order. `Align` also has `stretch` and `Justify` also
 *  has `space_between`, but neither is a position — both span or distribute along an axis rather than
 *  sitting somewhere on it — so they ride beside the grid as `AlignExtra` switches instead of
 *  pretending to be a tenth cell.
 *
 *  This replaced three tables of icons (`ALIGN_X`, `ALIGN_Y`, `JUSTIFY`) whose whole job was to make
 *  two 1-D enums add up to a 2-D picture in the author's head. The grid just draws the picture. */
const POSITIONS = ['start', 'center', 'end'] as const;

// The VALUES are logical (`start`/`end`); the GLYPHS are physical. RTL mirrors the artboard, so an
// `end`-aligned line moves to the left on a mirrored viewport while this icon keeps pointing right —
// which is correct: the icon names the field's value, not the pixel it lands on.
const TEXT_ALIGN = [
  { value: 'start', icon: AlignLeft },
  { value: 'center', icon: AlignCenter },
  { value: 'end', icon: AlignRight },
] as const;

// `none` is a real member of both groups, not an absence: a segmented control has no "off" state
// (see `ToggleGroup` — clicking the active item is swallowed), so without an explicit none cell an
// author could set a decoration and never clear it. The writer maps it back to `undefined`.
const DECORATION = [
  { value: 'underline', icon: Underline },
  { value: 'strike', icon: Strikethrough, title: 'strikethrough' },
  { value: 'none', icon: Ban },
] as const;

const TEXT_CASE = [
  { value: 'upper', icon: CaseUpper, title: 'UPPERCASE' },
  { value: 'title', icon: CaseSensitive, title: 'Title Case' },
  { value: 'lower', icon: CaseLower, title: 'lowercase' },
  { value: 'none', icon: Ban, title: 'as authored' },
] as const;

/** What a token RESOLVES to, for `Field.note`. Undefined for a dangling reference — the row already
 *  has an error for that, and a note reading "missing" would say it twice while pushing the real
 *  message down a line. */
function tokenNote(family: ThemeTokenFamily, token: string | undefined, theme: Theme): string | undefined {
  if (token === undefined) return undefined;
  const value = (theme as unknown as Record<string, Record<string, unknown>>)[family]?.[token];
  return value === undefined ? undefined : summarizeThemeToken(family, value, theme);
}

type SectionId =
  | 'content'
  | 'layout'
  | 'media'
  | 'typography'
  | 'size'
  | 'style'
  | 'motion'
  | 'interaction'
  | 'compat';

/** Exhaustive by construction: a new block kind will not compile until it is given an order. */
const ORDER: Record<BlockNode['block'], readonly SectionId[]> = {
  stack: ['layout', 'size', 'style', 'motion', 'interaction', 'compat'],
  box: ['layout', 'size', 'style', 'motion', 'interaction', 'compat'],
  scroll: ['layout', 'size', 'style', 'motion', 'interaction', 'compat'],
  grid: ['layout', 'size', 'style', 'motion', 'interaction', 'compat'],
  text: ['content', 'typography', 'size', 'style', 'motion', 'interaction', 'compat'],
  button: ['content', 'typography', 'size', 'style', 'motion', 'interaction', 'compat'],
  image: ['content', 'media', 'size', 'style', 'motion', 'interaction', 'compat'],
  video: ['content', 'media', 'size', 'style', 'motion', 'interaction', 'compat'],
  progress: ['content', 'size', 'style', 'motion', 'interaction', 'compat'],
  repeat: ['content', 'size', 'style', 'motion', 'interaction', 'compat'],
  custom: ['content', 'size', 'style', 'motion', 'interaction', 'compat'],
  // No content, no layout, no type, no media — a spacer really is only a size. Everything else it
  // still gets, because `BlockBase` still gives it.
  spacer: ['size', 'style', 'motion', 'interaction', 'compat'],
};

// ---------------------------------------------------------------- the fields that do nothing
//
// THE C2 RULE, and it is the highest-yield thing Phase C found: a field that is SET, shows a set-dot,
// and is silently ignored because ANOTHER field's value neutered it. The value is legal and still
// publishes — so the row must NOT disappear. It greys, keeps its value, and says WHICH field did it.
// Naming the culprit is the whole point; "disabled" on its own leaves the author nothing to change.
//
// Both predicates below are read straight off `measure.ts`, not guessed:

/** `aspect_ratio` is consulted ONLY by `intrinsicHeight` — i.e. only when the height is DERIVED. Any
 *  height that is not `hug` (an explicit number, or `fill`) makes it inert. The old panel caught only
 *  the `fill` half, which made the rest of the panel look more trustworthy than it was. */
function inertRatio(height: SizeVal | undefined): string | undefined {
  if (height === undefined || height === 'hug') return undefined;
  return height === 'fill'
    ? 'Ignored while the height is `fill` — the box is the leftover space, so the ratio decides nothing.'
    : `Ignored while the height is fixed (${height}) — you have already said how tall the box is, so the ratio decides nothing. Set the height to \`hug\` to size the image from its ratio.`;
}

/** `justify` distributes FREE SPACE along the main axis, and a hug has none: `freeMain` is
 *  `max(0, avail − total)`, which a hugging axis makes 0. Every value then resolves to offset 0. */
function inertJustify(direction: 'row' | 'column', size: Size | undefined): string | undefined {
  const main = direction === 'row' ? size?.width : size?.height;
  // `undefined` height means hug (the default); `undefined` width does NOT — `width` is required by
  // `Size`, and a stack with no `size` at all still hugs its height. Both cases land here as "hug".
  if (main !== undefined && main !== 'hug') return undefined;
  const axis = direction === 'row' ? 'width' : 'height';
  return `Nothing to distribute — this stack hugs its ${axis}, so there is no free space along the ${direction === 'row' ? 'horizontal' : 'vertical'} axis. Give it a ${axis} (a number, or \`fill\`) and \`justify\` starts working.`;
}

const TITLE: Record<SectionId, string> = {
  content: 'Content',
  layout: 'Layout',
  media: 'Media',
  typography: 'Type',
  size: 'Size',
  style: 'Style',
  motion: 'Motion',
  interaction: 'Interaction',
  compat: 'Compatibility',
};

/** IS THIS SECTION CARRYING ANYTHING? A section with nothing set opens as a bare header row.
 *
 *  This is what pays for removing the design/settings tabs. R1 still holds — the section is still
 *  THERE, still in the order, still one click away — it just does not spend a body's worth of column
 *  saying that every one of its fields is absent. `motion`, `interaction` and `compat` are unset on
 *  very nearly every node, which is precisely why they were the three exiled to a second tab.
 *
 *  Deliberately NOT computed by walking the rendered rows: a section's fields are the ones named
 *  here, and reading them off the node is the same source `Disclosure` uses per row. Sections whose
 *  block ALWAYS has content (`layout`, `size`, `content`, `media`, `typography`, `style`) are never
 *  quiet — a `stack` always has a direction, an `image` always has a source. */
function isQuiet(id: SectionId, node: BlockNode): boolean {
  switch (id) {
    case 'motion':
      // `animate` only. `backdrop` used to be authored here too and counted toward this — it now
      // lives in Style, and leaving it in would render Motion open for a node whose only style is a
      // blur the section no longer owns.
      return node.style?.animate === undefined;
    case 'interaction':
      return (
        (node.actions?.length ?? 0) === 0 &&
        node.a11y_label === undefined &&
        node.a11y_hidden === undefined
      );
    case 'compat':
      return (node.requires?.length ?? 0) === 0 && node.fallback === undefined;
    default:
      return false;
  }
}

export function BlockSections({
  node,
  parent,
  root,
  labels,
  step,
  theme,
  set,
  inScroll,
  onSelect,
  selectedFrame,
}: {
  node: BlockNode;
  parent: BlockNode | null;
  root: BlockNode | null;
  labels: AuthoringDoc['labels'];
  step?: StepCtx | undefined;
  theme: Theme;
  set: Setter;
  inScroll: boolean;
  onSelect: (id: string) => void;
  selectedFrame: Frame | null;
}): React.JSX.Element {
  const issues: FieldIssues = nodeIssues(node, parent, theme);
  const props = { node, parent, root, labels, step, theme, set, issues, inScroll, onSelect, selectedFrame };

  // ONE COLUMN, IN `ORDER`. There used to be a `design`/`settings` tab strip here, splitting the
  // table on `['content', 'interaction', 'compat']` — which put the TEXT of a `text` node and the
  // SOURCE of an `image` behind a tab, while Type and Size led the panel. `ORDER` already reads
  // `content` first for every block that has content; the filter was scrambling a hierarchy that was
  // authored correctly. What the tabs actually bought was vertical space, and `isQuiet` buys it back
  // without hiding anything: an unset section costs a header row, not a click.
  return (
    <>
      {ORDER[node.block].map((id) => (
        <SectionBody key={id} id={id} {...props} />
      ))}
    </>
  );
}

const NONE_SCROLL = '—';

/** Every `scroll` in the step, with the number of cards it holds — which is exactly the number of
 *  dots that may track it (SPEC §7 / `checkProgressTracks`) — and a human LABEL.
 *
 *  The label is what the layers tree calls it (`nodeLabel`, so a renamed scroll reads the same in
 *  both places), suffixed with its card count. Without this the `tracks` picker rendered the scroll's
 *  raw UUID — `4cbe-aaba-fc20714e70e8` — which tells an author nothing about which scroll they are
 *  binding to, and the card count is exactly the thing they need to see, because picking one SETS
 *  `segments` to it. */
function collectScrolls(root: BlockNode, labels: AuthoringDoc['labels']): { id: string; pages: number; label: string }[] {
  const found: { id: string; pages: number; label: string }[] = [];
  const walk = (n: BlockNode): void => {
    if (n.block === 'scroll') {
      const cards = n.children.length;
      found.push({ id: n.id, pages: cards, label: `${nodeLabel(n, labels)} · ${cards} card${cards === 1 ? '' : 's'}` });
    }
    if ('children' in n) n.children.forEach(walk);
  };
  walk(root);
  return found;
}

interface SectionProps {
  node: BlockNode;
  parent: BlockNode | null;
  /** The step, for the ROOT's height law — it needs the surface, and only the document sees both. */
  step?: StepCtx | undefined;
  /** The step's ROOT. `progress.tracks` is a document-level relationship — it names a scroll that
   *  may be anywhere in the step — so it cannot be offered from the node alone. */
  root: BlockNode | null;
  /** The author's names, so `tracks` can show a scroll the same way the layers tree does. */
  labels: AuthoringDoc['labels'];
  theme: Theme;
  set: Setter;
  issues: FieldIssues;
  inScroll: boolean;
  /** So the placement readout can hand the author the control it is describing. */
  onSelect: (id: string) => void;
  /** The solver's rect for this node, so `fill`/`hug` can say what they RESOLVED to. */
  selectedFrame: Frame | null;
}

/** WHERE AM I, AND WHO DECIDED. Alignment is a CONTAINER property — `box.align_x`/`align_y` and
 *  `stack.align`/`justify` place every child at once — so a child cannot own a placement grid without
 *  silently moving its siblings.
 *
 *  The honest alternative is to say so. This names the value that governs this node, names the field
 *  it lives in, and hands over the control in one click, using the same greyed treatment as C2's
 *  `inert`: a real value, visible, not editable HERE. Without it a `stretch`ed child in a `box` looks
 *  like it decided its own width and the panel has no row that explains it. */
function PlacedBy({ parent, onSelect }: { parent: BlockNode | null; onSelect: (id: string) => void }): React.JSX.Element | null {
  const placement =
    parent?.block === 'box'
      ? `align x ${parent.align_x} · align y ${parent.align_y}`
      : parent?.block === 'stack'
        ? `justify ${parent.justify} · align ${parent.align}`
        : null;
  if (!parent || placement === null) return null;

  return (
    <p className="flex items-center gap-1 pt-0.5 text-[11px] leading-snug text-muted-foreground">
      <span className="opacity-70">
        Placed by the parent {parent.block} — {placement}.
      </span>
      <button
        type="button"
        onClick={() => onSelect(parent.id)}
        className="shrink-0 rounded text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        Select it
      </button>
    </p>
  );
}

function SectionBody({ id, ...p }: { id: SectionId } & SectionProps): React.JSX.Element | null {
  const styleIssue = Object.keys(p.issues).some((k) => k.startsWith('style.'));

  switch (id) {
    case 'content':
      return (
        <Section title={TITLE.content}>
          <ContentSection {...p} />
        </Section>
      );
    case 'layout':
      return (
        <Section title={p.node.block === 'stack' ? 'Auto layout' : TITLE.layout}>
          <LayoutSection {...p} />
        </Section>
      );
    case 'media':
      return (
        <Section title={TITLE.media}>
          <MediaSection {...p} />
        </Section>
      );
    case 'typography':
      return (
        <Section title={TITLE.typography}>
          <TypographySection {...p} />
        </Section>
      );
    case 'size':
      return (
        <Section title={TITLE.size}>
          <SizeField
            node={p.node}
            parent={p.parent}
            step={p.step}
            size={p.node.size}
            onChange={(size, field) => p.set(field ?? 'size', (n) => (size ? (n.size = size) : delete n.size))}
            errors={{ width: p.issues['size.width'], height: p.issues['size.height'] }}
            frame={p.selectedFrame}
          />
          <PlacedBy parent={p.parent} onSelect={p.onSelect} />
        </Section>
      );
    case 'style':
      return (
        <Section title={TITLE.style} hasIssue={styleIssue}>
          <StyleSection {...p} />
        </Section>
      );
    // These three render their own `<Section>` (motion and compat both write fields their bodies
    // own), so `quiet` is threaded rather than wrapped here.
    case 'motion':
      return (
        <MotionField
          node={p.node}
          set={p.set}
          issues={p.issues}
          inScroll={p.inScroll}
          quiet={isQuiet('motion', p.node)}
        />
      );
    case 'interaction':
      return <InteractionSection {...p} quiet={isQuiet('interaction', p.node)} />;
    case 'compat':
      return <CompatSection {...p} quiet={isQuiet('compat', p.node)} />;
  }
}

type Setter = <T extends BlockNode>(field: string, mutate: (node: T, doc: AuthoringDoc) => void) => void;

/** MINT AND ASSIGN IN ONE `applyChange` — the detail that decides whether this feels native.
 *
 *  `mintColorToken` writes the token into `doc.theme` and `assign` writes the reference onto the
 *  node, both inside the same immer draft, so the inverse patch removes BOTH. ⌘Z has to undo "I used
 *  a new colour"; a two-mutation implementation would undo the assignment and leave an orphan token
 *  in a theme the author never opened.
 *
 *  Curried so a call site reads as one line next to the `onChange` it mirrors. */
function mintInto<T extends BlockNode>(
  set: Setter,
  field: string,
  assign: (node: T, token: string) => void
): (light: string, dark: string) => void {
  return (light, dark) => set<T>(field, (node, doc) => assign(node, mintColorToken(doc, light, dark)));
}

/** Does this block name ITSELF? `text` and `button` derive their a11y label from their own spans
 *  (SPEC §5.0a), so a missing `a11y_label` there is fine — the nag is for everything else. */
const hasOwnText = (n: BlockNode): boolean => n.block === 'text' || n.block === 'button';
// ---------------------------------------------------------------------------- layout

function LayoutSection({ node, set, issues }: SectionProps): React.JSX.Element | null {
  switch (node.block) {
    case 'stack': {
      // Computed once and shared by the disabled state, the hint, and the one-click fix below.
      const justifyHint = inertJustify(node.direction, node.size);
      const mainAxis: 'width' | 'height' = node.direction === 'row' ? 'width' : 'height';
      // "Equal sizes" is the OTHER reading of "distribute equally" (space_between is the first): give
      // every child the same main-axis extent via per-child `fill`, which the solver splits evenly
      // (measure.ts `distributeMain`). It is a real, multi-child capability the panel only ever
      // exposed as a per-node property nobody found. Only children that can LEGALLY take `fill` on
      // the main axis are offered up — e.g. a `progress` height is number-only — so the action can
      // never author an unpublishable document. Meaningless below two such children.
      const fillableChildIds = node.children
        .filter((child) => sizeOptions(child, node)[mainAxis].includes('fill'))
        .map((child) => child.id);

      // WHICH SCREEN AXIS IS THE MAIN ONE. This is the single place that knows, so the grid itself
      // never has to. A `row` runs left→right, so `justify` drives the grid's COLUMNS; a `column`
      // runs top→bottom, so it drives the ROWS.
      const isRow = node.direction === 'row';
      // `space_between` and `stretch` are not POSITIONS, so they light no cell — their `AlignExtra`
      // switch is on instead, and clicking a cell replaces them.
      const justifyPos = node.justify === 'space_between' ? undefined : node.justify;
      const alignPos = node.align === 'stretch' ? undefined : node.align;
      // ONE mutation for both fields: a placement is a single decision, so it is a single undo entry.
      const setPlacement = (justify: Justify, align: Align): void =>
        set('placement', (n) => {
          const stack = n as typeof node;
          stack.justify = justify;
          stack.align = align;
        });
      return (
        <>
          <Field label="Flow" stacked>
            <ToggleGroup
              label="direction"
              fill
              value={node.direction}
              options={[
                { value: 'column', icon: ArrowDown },
                { value: 'row', icon: ArrowRight },
              ]}
              onChange={(direction) => set('direction', (n) => void ((n as typeof node).direction = direction))}
            />
          </Field>
          <Scrub label="Gap" value={node.gap} onChange={(gap) => set('gap', (n) => void ((n as typeof node).gap = gap))} />
          {/* ALIGN IS THE CROSS AXIS, JUSTIFY IS THE MAIN ONE — so which SCREEN axis each one drives
              FLIPS with `direction`. The old panel expressed that by swapping the icon set on two
              separate rows, which is correct and still asks the author to hold two 1-D enums in their
              head to picture one 2-D placement. The grid maps both to screen at once, and the flip is
              the caller's `isRow` below — the one place that knows which way the stack runs.
              `justify` keeps its C2 inert treatment: a stack that HUGS its main axis has no free
              space to distribute, so that AXIS of the grid locks and the fix stays one click away. */}
          <Field label="Alignment" stacked inert={justifyHint}>
            <div className="flex w-full items-stretch gap-2">
              <AlignGrid
                label="placement"
                columns={POSITIONS}
                rows={POSITIONS}
                spacious
                x={isRow ? justifyPos : alignPos}
                y={isRow ? alignPos : justifyPos}
                // THE LOCK IS VISIBLE, not merely enforced: `lockedAxis` disables every cell off the
                // frozen value, so a click cannot light up a cell other than the one clicked. The
                // `justifyHint` guard in `onChange` stays as the value-level backstop — `justify` may
                // hold `space_between`, which has no cell and could never survive a round trip
                // through the grid's own value. The cross axis stays live, which is the point of
                // locking an axis rather than disabling the control.
                lockedAxis={justifyHint ? (isRow ? 'x' : 'y') : undefined}
                onChange={(cx, cy) =>
                  setPlacement(justifyHint ? node.justify : isRow ? cx : cy, isRow ? cy : cx)
                }
              />
              <div className="flex w-[96px] shrink-0 flex-col gap-1 [&>button]:w-full">
                <AlignExtra
                  label="space between"
                  on={node.justify === 'space_between'}
                  disabled={justifyHint !== undefined}
                  onChange={(on) => setPlacement(on ? 'space_between' : 'start', node.align)}
                />
                <AlignExtra
                  label="stretch"
                  on={node.align === 'stretch'}
                  onChange={(on) => setPlacement(node.justify, on ? 'stretch' : 'start')}
                />
                {/* ...and the fix is one click, HERE. The dependency is cross-section — placement
                    lives in Layout, the width that frees it lives in Size a row down — which is
                    exactly why a locked axis reads as the panel being stubborn rather than as "give
                    it a width". Filling the main axis is the smallest edit that makes `justify` live,
                    so offer it in place. Undo-keyed as a size edit, same as SizeField. */}
                {justifyHint ? (
                  <button
                    type="button"
                    onClick={() =>
                      set(`size.${mainAxis}`, (n) => {
                        const current = (n as typeof node).size;
                        const next: Size = current ? { ...current } : { width: 'fill' };
                        next[mainAxis] = 'fill';
                        (n as typeof node).size = next;
                      })
                    }
                    className="h-8 shrink-0 whitespace-nowrap rounded-lg border border-border bg-muted/70 px-2 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    Fill {mainAxis}
                  </button>
                ) : null}
              </div>
            </div>
          </Field>
          {/* DISTRIBUTE EQUALLY, second reading: equal child SIZES (space_between above is equal
              gaps). One click sizes the main axis to `fill` (so there is space to divide) and gives
              each eligible child `fill`, which the solver splits evenly. Only shown when at least two
              children can take it. */}
          {fillableChildIds.length >= 2 ? (
            <Field label="Distribution">
              <button
                type="button"
                onClick={() =>
                  set('distribute', (n) => {
                    const stack = n as typeof node;
                    const current = stack.size;
                    const next: Size = current ? { ...current } : { width: 'fill' };
                    next[mainAxis] = 'fill';
                    stack.size = next;
                    for (const child of stack.children) {
                      if (!fillableChildIds.includes(child.id)) continue;
                      const childSize: Size = child.size ? { ...child.size } : { width: 'fill' };
                      childSize[mainAxis] = 'fill';
                      child.size = childSize;
                    }
                  })
                }
                className="h-8 shrink-0 whitespace-nowrap rounded-lg border border-border bg-muted/70 px-2 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                Equal sizes
              </button>
            </Field>
          ) : null}
          <PaddingField padding={node.padding} onChange={(padding) => setPadding(set, padding)} />
        </>
      );
    }

    case 'box': {
      // A Z-stack places children by ALIGNMENT, never coordinates (SPEC §5.2) — which is exactly what
      // makes a 3×3 grid the honest control here rather than a convenience: the nine cells ARE the
      // expressible space, and there is no tenth place a child could go.
      const setPlacement = (align_x: Align, align_y: Align): void =>
        set('placement', (n) => {
          const box = n as typeof node;
          box.align_x = align_x;
          box.align_y = align_y;
        });
      return (
        <>
          <Field label="Alignment" stacked>
            <div className="flex w-full items-stretch gap-2">
              <AlignGrid
                label="placement"
                columns={POSITIONS}
                rows={POSITIONS}
                spacious
                x={node.align_x === 'stretch' ? undefined : node.align_x}
                y={node.align_y === 'stretch' ? undefined : node.align_y}
                onChange={setPlacement}
              />
              <div className="flex w-[96px] shrink-0 flex-col gap-1 [&>button]:w-full">
                <AlignExtra
                  label="stretch x"
                  on={node.align_x === 'stretch'}
                  onChange={(on) => setPlacement(on ? 'stretch' : 'start', node.align_y)}
                />
                <AlignExtra
                  label="stretch y"
                  on={node.align_y === 'stretch'}
                  onChange={(on) => setPlacement(node.align_x, on ? 'stretch' : 'start')}
                />
              </div>
            </div>
          </Field>
          <PaddingField padding={node.padding} onChange={(padding) => setPadding(set, padding)} />
        </>
      );
    }

    case 'scroll':
      return (
        <>
          <Field label="axis">
            <ToggleGroup
              label="axis"
              value={node.axis}
              options={[
                { value: 'vertical', icon: ArrowDown },
                { value: 'horizontal', icon: ArrowRight },
              ]}
              onChange={(axis) => set('axis', (n) => void ((n as typeof node).axis = axis))}
            />
          </Field>
          <Field label="snap" set={node.snap === true}>
            <BoolInput
              label="snap to cards"
              value={node.snap}
              onChange={(snap) =>
                set('snap', (n) => {
                  const t = n as typeof node;
                  if (snap) t.snap = true;
                  else {
                    delete t.snap;
                    // THE SCHEMA REJECTS `auto_advance_ms` WITHOUT `snap` — there are no snap points
                    // to advance between. Clearing snap while a timing survives would author a
                    // document that cannot publish, so the timing goes with it.
                    delete t.auto_advance_ms;
                  }
                })
              }
            />
          </Field>
          {/* THE FIELD THAT MAKES A CAROUSEL A CAROUSEL, and it was unauthorable. It is offered only
              once `snap` is on, because the schema will not take one without the other (B3). */}
          {node.snap ? (
            <Field
              label="auto advance"
              error={issues['auto_advance_ms']}
              set={node.auto_advance_ms !== undefined}
              onUnset={() => set('auto_advance_ms', (n) => void delete (n as typeof node).auto_advance_ms)}
            >
              <NumberInput
                aria-label="auto advance"
                min={1000}
                max={60_000}
                step={500}
                value={node.auto_advance_ms}
                onChange={(v) =>
                  set('auto_advance_ms', (n) => {
                    const t = n as typeof node;
                    if (v === undefined) delete t.auto_advance_ms;
                    else t.auto_advance_ms = v;
                  })
                }
              />
              <span className="text-[11px] text-muted-foreground">ms</span>
            </Field>
          ) : null}
        </>
      );

    case 'grid':
      return (
        <>
          <Field label="cols" error={issues['cols']}>
            <NumberInput
              aria-label="cols"
              min={1}
              max={12}
              value={node.cols}
              onChange={(cols) => set('cols', (n) => void ((n as typeof node).cols = cols ?? 1))}
            />
          </Field>
          <Scrub label="h spacing" value={node.h_spacing} onChange={(v) => set('h_spacing', (n) => void ((n as typeof node).h_spacing = v))} />
          <Scrub label="v spacing" value={node.v_spacing} onChange={(v) => set('v_spacing', (n) => void ((n as typeof node).v_spacing = v))} />
          <PaddingField padding={node.padding} onChange={(padding) => setPadding(set, padding)} />
        </>
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------- content

function ContentSection({ node, root, labels, theme, set, issues }: SectionProps): React.JSX.Element | null {
  switch (node.block) {
    case 'text':
      return (
        <SpansField
          spans={node.spans}
          errors={issues}
          onChange={(spans) => set('spans', (n) => void ((n as typeof node).spans = spans))}
        />
      );

    case 'button':
      return (
        <SpansField
          spans={node.label}
          errors={issues}
          onChange={(label) => set('label', (n) => void ((n as typeof node).label = label))}
        />
      );

    case 'image':
    case 'video':
      return (
        <AssetField
          label="url"
          url={node.source.kind === 'url' ? node.source.url : ''}
          assetId={node.source.kind === 'asset' ? node.source.asset_id : undefined}
          onChange={(url) => set('source', (n) => void ((n as typeof node).source = { kind: 'url', url }))}
          onAssetChange={(asset_id) => set('source', (n) => void ((n as typeof node).source = { kind: 'asset', asset_id }))}
        />
      );

    case 'custom':
      return (
        <>
          <Field label="identifier" error={issues['identifier']}>
            <TextInput aria-label="identifier" value={node.identifier} onChange={(identifier) => set('identifier', (n) => void ((n as typeof node).identifier = identifier))} />
          </Field>
          {/* The canvas stubs any identifier (F1), so a typo LOOKS fine here — but on a device whose
              host has not registered it, the block is dropped (SPEC §7.1). The editor cannot know the
              host's registry, so this says so rather than pretending to validate it. C4. */}
          <p className="text-[11px] leading-5 text-muted-foreground">
            This name must match a component the host app registers, or the block is dropped on the device. Add a fallback to control what shows instead.
          </p>
          <ConfigField config={node.config} onChange={(config) => set('config', (n) => void ((n as typeof node).config = config))} />
        </>
      );

    case 'progress':
      return <ProgressContent node={node} root={root} labels={labels} theme={theme} set={set} issues={issues} />;

    case 'repeat': {
      // Both arms of `source` are a KEY into the SDK's items map (SPEC §7.2): `campaigns` reads the
      // SDK's own eligible-campaign list, `host` reads a list the app pushed with setItems(). One
      // field names the key either way — which is why they share an input.
      const kind = node.source.kind;
      const key = node.source.kind === 'host' ? node.source.key : node.source.collection;
      return (
        <>
          <Field label="source">
            <SelectInput
              aria-label="source"
              value={kind}
              options={['campaigns', 'host'] as const}
              onChange={(next) =>
                set('source', (n) => {
                  (n as typeof node).source =
                    next === 'host' ? { kind: 'host', key } : { kind: 'campaigns', collection: key };
                })
              }
            />
          </Field>
          <Field label={kind === 'host' ? 'key' : 'collection'} error={issues['source.key'] ?? issues['source.collection']}>
            <TextInput
              aria-label={kind === 'host' ? 'key' : 'collection'}
              value={key}
              onChange={(k) =>
                set('source', (n) => {
                  (n as typeof node).source =
                    kind === 'host' ? { kind: 'host', key: k } : { kind: 'campaigns', collection: k };
                })
              }
            />
          </Field>
          <Field label="max" error={issues['max']}>
            <NumberInput aria-label="max" min={1} max={50} value={node.max} onChange={(v) => set('max', (n) => void ((n as typeof node).max = v ?? 1))} />
          </Field>
          <p className="text-[11px] text-muted-foreground">
            {kind === 'host'
              ? 'The app pushes this list once with setItems(). Static items — no fetch, no refresh.'
              : 'Repeats over the SDK’s own eligible campaigns. Held locally; no network.'}{' '}
            The template is one block, repeated per item. Editing it on the canvas is a later slice.
          </p>
        </>
      );
    }

    default:
      return null;
  }
}

/** `progress` is its own component only because `tracks` needs the whole step to offer a choice, and
 *  threading the step through every section for one field would be worse than one prop here. */
function ProgressContent({
  node,
  root,
  labels,
  theme,
  set,
  issues,
}: {
  node: Extract<BlockNode, { block: 'progress' }>;
  root: BlockNode | null;
  labels: AuthoringDoc['labels'];
  theme: Theme;
  set: Setter;
  issues: FieldIssues;
}): React.JSX.Element {
  const scrolls = root ? collectScrolls(root, labels) : [];

  return (
    <>
      {/* THE DOTS THAT FOLLOW THE CARDS — and the other field that made a carousel a carousel while
          being unauthorable (B3).

          A PICKER OF THE SCROLLS IN THIS STEP, NEVER A FREE-TEXT FIELD. Publish already rejects a
          `tracks` that names something which is not a scroll, so an input that lets you type one is
          an editor that lies.

          And it MUST set `segments` too: the schema requires `segments === scroll.children.length`
          ("the dots would lie" — checkProgressTracks). Offering the id without the count would hand
          the author a document that cannot publish, from a control whose whole job is to connect the
          two. */}
      <Field
        label="tracks"
        error={issues['tracks']}
        set={node.tracks !== undefined}
        onUnset={() => set('tracks', (n) => void delete (n as typeof node).tracks)}
      >
        <SelectInput
          aria-label="tracks"
          value={node.tracks ?? NONE_SCROLL}
          options={[NONE_SCROLL, ...scrolls.map((s) => s.id)]}
          // A scroll's UUID is not a name. Show the same thing the layers tree does — and if the id
          // is dangling (the scroll was deleted after it was tracked), fall through to the raw value
          // rather than a blank, so the broken state is at least visible.
          labels={{ [NONE_SCROLL]: 'none', ...Object.fromEntries(scrolls.map((s) => [s.id, s.label])) }}
          onChange={(id) =>
            set('tracks', (n) => {
              const t = n as typeof node;
              if (id === NONE_SCROLL) return void delete t.tracks;
              t.tracks = id;
              t.segments = scrolls.find((s) => s.id === id)?.pages ?? t.segments;
              if (t.value > t.segments) t.value = t.segments;
            })
          }
        />
      </Field>
      {scrolls.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">
          No scroll in this step to track. Add one, and its cards become these dots.
        </p>
      ) : null}

      <Field label="segments" error={issues['segments']}>
        <NumberInput
          aria-label="segments"
          min={1}
          max={32}
          // Owned by `tracks` when there is one: the count IS the scroll's card count, and letting
          // the author drift it apart only authors the lie the schema is there to prevent.
          disabled={node.tracks !== undefined}
          value={node.segments}
          onChange={(v) => set('segments', (n) => void ((n as typeof node).segments = v ?? 1))}
        />
      </Field>
      <Field label="value" error={issues['value']}>
        <NumberInput aria-label="value" min={0} value={node.value} onChange={(v) => set('value', (n) => void ((n as typeof node).value = v ?? 0))} />
      </Field>
      <Field label="gap">
        <NumberInput aria-label="gap" min={0} value={node.gap} onChange={(v) => set('gap', (n) => void ((n as typeof node).gap = v ?? 0))} />
      </Field>
      <Field label="fill color" error={issues['fill_color']}>
        <TokenPicker label="fill color" kind="colors" theme={theme} value={node.fill_color} onChange={(c) => c && set('fill_color', (n) => void ((n as typeof node).fill_color = c))} onMint={mintInto(set, 'fill_color', (n: typeof node, t) => void (n.fill_color = t))} />
      </Field>
      <Field label="track color" error={issues['track_color']}>
        <TokenPicker label="track color" kind="colors" theme={theme} value={node.track_color} onChange={(c) => c && set('track_color', (n) => void ((n as typeof node).track_color = c))} onMint={mintInto(set, 'track_color', (n: typeof node, t) => void (n.track_color = t))} />
      </Field>
    </>
  );
}

// ---------------------------------------------------------------------------- media

function MediaSection({ node, theme, set, issues }: SectionProps): React.JSX.Element | null {
  if (node.block === 'image') {
    return (
      <>
        {/* REQUIRED, and no `hug` width: intrinsic sizing would make layout depend on the image
            bytes, so the box would jump on decode.

            IT IS INERT WHENEVER THE HEIGHT DOES NOT HUG — and the `fill` case was only HALF of it.
            `aspect_ratio` is read in exactly one place (`measure.ts` → `intrinsicHeight`), which runs
            only when the height is DERIVED. Give the image an explicit `height: 120` and the ratio
            decides nothing at all — and the panel used to keep the field live, with its set-dot, for
            that case. Phase C found it by driving the product.

            So the test is `height !== hug`, not `height === fill`, and the row now names the field
            that neutered it rather than merely greying out. */}
        <Field label="aspect ratio" error={issues['aspect_ratio']} inert={inertRatio(node.size?.height)}>
          <NumberInput
            aria-label="aspect ratio"
            step={0.01}
            min={0.01}
            disabled={inertRatio(node.size?.height) !== undefined}
            value={node.aspect_ratio}
            onChange={(v) => set('aspect_ratio', (n) => void ((n as typeof node).aspect_ratio = v ?? 1))}
          />
        </Field>
        {/* THE WORD "fill" MEANS THREE THINGS IN THIS PANEL, and one of them is here. A size `fill`
            takes the leftover space; `fit: "fill"` STRETCHES the pixels. The wire value cannot
            change (it is in a pinned binary), so the LABEL does the work — and now so does a GLYPH,
            which is the whole reason this control stopped being a native `<select>`. */}
        <Field label="fit">
          <SelectInput
            aria-label="fit"
            value={node.fit}
            options={['cover', 'contain', 'fill'] as const}
            // SHORT, because the GLYPH now carries the meaning — "cover — crop to fill" wrapped to two
            // lines in a 150px control and made this the tallest row in the panel. And note the wire
            // value `fill` still renders as "stretch": that single word does more to kill the
            // "fill means three different things in this panel" confusion than the dash-clause did.
            labels={{ cover: 'cover', contain: 'contain', fill: 'stretch' }}
            icons={{ cover: Crop, contain: Shrink, fill: Expand }}
            onChange={(fit) => set('fit', (n) => void ((n as typeof node).fit = fit))}
          />
        </Field>
        {/* "image radius", not "radius". `image.radius` (this) and `style.radius` (the Style section)
            are different fields, and BOTH rendered as a row labelled "radius" in the same panel. */}
        <Field label="image radius" set={node.radius !== undefined}>
          <NumberInput aria-label="image radius" min={0} value={typeof node.radius === 'number' ? node.radius : undefined} onChange={(radius) => set('radius', (n) => void ((n as typeof node).radius = radius))} />
        </Field>
        <Field label="tint" error={issues['tint']}>
          <TokenPicker label="tint" kind="colors" theme={theme} optional value={node.tint} onChange={(tint) => set('tint', (n) => { if (tint) (n as typeof node).tint = tint; else delete (n as typeof node).tint; })} onMint={mintInto(set, 'tint', (n: typeof node, t) => void (n.tint = t))} />
        </Field>
        {/* A cover crop keeps the CENTRE, which is exactly where faces are not (SPEC §5.0d).
            Only offered at fit:"cover" — nothing is cropped otherwise, and the schema rejects it. */}
        {node.fit === 'cover' ? (
          <Field label="focal" error={issues['focal']} set={node.focal !== undefined}>
            <NumberInput
              className="w-[56px]"
              aria-label="focal x"
              min={0}
              max={1}
              step={0.05}
              value={node.focal?.x}
              onChange={(x) =>
                set('focal', (n) => {
                  const t = n as typeof node;
                  if (x === undefined) delete t.focal;
                  else t.focal = { x, y: t.focal?.y ?? 0.5 };
                })
              }
            />
            <NumberInput
              className="w-[56px]"
              aria-label="focal y"
              min={0}
              max={1}
              step={0.05}
              value={node.focal?.y}
              onChange={(y) =>
                set('focal', (n) => {
                  const t = n as typeof node;
                  if (y === undefined) delete t.focal;
                  else t.focal = { x: t.focal?.x ?? 0.5, y };
                })
              }
            />
          </Field>
        ) : null}
      </>
    );
  }

  if (node.block === 'video') {
    return (
      <>
        {/* Same law as the image above: `video` derives its hug height from `aspect_ratio` through the
            SAME `intrinsicHeight` path (`measure.ts` §5.6), so it is inert whenever the height does not
            hug. #123 guarded the image and left the video field live — one predicate closes it. */}
        <Field label="aspect ratio" error={issues['aspect_ratio']} inert={inertRatio(node.size?.height)}>
          <NumberInput aria-label="aspect ratio" step={0.01} min={0.01} disabled={inertRatio(node.size?.height) !== undefined} value={node.aspect_ratio} onChange={(v) => set('aspect_ratio', (n) => void ((n as typeof node).aspect_ratio = v ?? 1))} />
        </Field>
        <Field label="poster">
          <TextInput aria-label="poster" value={node.poster ?? ''} onChange={(poster) => set('poster', (n) => void ((n as typeof node).poster = poster || undefined))} />
        </Field>
        <Field label="playback">
          <span className="flex gap-2">
            <BoolInput label="autoplay" value={node.autoplay} onChange={(v) => set('autoplay', (n) => void ((n as typeof node).autoplay = v))} />
            <BoolInput label="muted" value={node.muted} onChange={(v) => set('muted', (n) => void ((n as typeof node).muted = v))} />
            <BoolInput label="loop" value={node.loop} onChange={(v) => set('loop', (n) => void ((n as typeof node).loop = v))} />
          </span>
        </Field>
        <Field label="video radius" set={node.radius !== undefined}>
          <NumberInput aria-label="video radius" min={0} value={typeof node.radius === 'number' ? node.radius : undefined} onChange={(radius) => set('radius', (n) => void ((n as typeof node).radius = radius))} />
        </Field>
      </>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------- typography

function TypographySection({ node, theme, set, issues }: SectionProps): React.JSX.Element | null {
  if (node.block === 'text') {
    const truncating = node.max_lines !== undefined;
    return (
      <>
        <Field label="Font" error={issues['font']}>
          <TokenPicker label="font" kind="fonts" theme={theme} value={node.font} description={tokenNote('fonts', node.font, theme)} onChange={(font) => font && set('font', (n) => void ((n as typeof node).font = font))} onVariant={(next) => set('font', (n, doc) => void ((n as typeof node).font = mintFontVariant(doc, node.font, next)))} />
        </Field>
        <Field label="Color" error={issues['color']}>
          <TokenPicker label="color" kind="colors" theme={theme} value={node.color} description={tokenNote('colors', node.color, theme)} onChange={(color) => color && set('color', (n) => void ((n as typeof node).color = color))} onMint={mintInto(set, 'color', (n: typeof node, t) => void (n.color = t))} />
        </Field>
        {/* A dropdown of three near-identical words is exactly the control `SelectInput`'s own
            comment argues against. The VALUES stay logical (`start`/`end`) — RTL mirrors the
            artboard, not the icons, so the glyph is a hint and the enum is the truth. */}
        <Field label="Align">
          <ToggleGroup
            label="text align"
            value={node.align ?? 'start'}
            options={TEXT_ALIGN}
            onChange={(align) => set('align', (n) => void ((n as typeof node).align = align))}
          />
        </Field>
        {/* PAINT-ONLY (SPEC §5.5) — it moves no rectangle, which is why it costs the solver nothing
            and every port only has to draw it. */}
        <Field label="Decoration" set={node.decoration !== undefined}>
          <ToggleGroup
            label="decoration"
            value={node.decoration ?? 'none'}
            options={DECORATION}
            onChange={(d) => set('decoration', (n) => void ((n as typeof node).decoration = d === 'none' ? undefined : d))}
          />
        </Field>
        {/* Casing runs BEFORE measurement (SPEC §6.4c), so this genuinely changes the layout — and it
            has to live on the node rather than in the literal, because a `ref` span's value is only
            known at runtime. */}
        <Field label="Text case" set={node.text_case !== undefined}>
          <ToggleGroup
            label="text case"
            value={node.text_case ?? 'none'}
            options={TEXT_CASE}
            onChange={(c) => set('text_case', (n) => void ((n as typeof node).text_case = c === 'none' ? undefined : c))}
          />
        </Field>
        {/* Empty = uncapped. The author sees "Alex" at design time and ships a card that breaks
            for "Bartholomew-Constantine" — this is the field that stops that (SPEC §6.11).
            A bare number whose empty state silently meant "off" never said that; the mode does. */}
        <Field label="Truncate" set={truncating}>
          <div className="grid min-w-0 flex-1 grid-cols-[96px_minmax(0,1fr)] gap-1">
            <SelectInput
              aria-label="truncate"
              value={truncating ? 'lines' : 'off'}
              options={['off', 'lines'] as const}
              labels={{ off: 'off', lines: 'after' }}
              onChange={(mode) =>
                set('max_lines', (n) => void ((n as typeof node).max_lines = mode === 'off' ? undefined : ((node.max_lines ?? 2))))
              }
              className="w-full"
            />
          {/* MOUNTED EVEN WHEN OFF, greyed. Two reasons that agree: C2's visibility half — a
              neutered field stays on screen rather than vanishing — and the inventory golden's
              first pass is a PURE READ, so a conditionally-rendered count would silently vanish
              from the contract. (Only the visibility half: the VALUE is not preserved across
              off→on, because mode and count share `max_lines` — off clears it, on reseeds 2.) */}
            <NumberInput
              aria-label="max lines"
              min={1}
              value={node.max_lines}
              disabled={!truncating}
              className="w-full"
              onChange={(v) => set('max_lines', (n) => void ((n as typeof node).max_lines = v))}
            />
          </div>
        </Field>
      </>
    );
  }

  if (node.block === 'button') {
    return (
      <>
        <Field label="variant" error={issues['variant']} note={tokenNote('buttons', node.variant, theme)}>
          <TokenPicker label="variant" kind="buttons" theme={theme} value={node.variant} onChange={(variant) => variant && set('variant', (n) => void ((n as typeof node).variant = variant))} />
        </Field>
        <Field label="font" error={issues['font']} note={tokenNote('fonts', node.font, theme)}>
          <TokenPicker label="font" kind="fonts" theme={theme} value={node.font} onChange={(font) => font && set('font', (n) => void ((n as typeof node).font = font))} onVariant={(next) => set('font', (n, doc) => void ((n as typeof node).font = mintFontVariant(doc, node.font, next)))} />
        </Field>
        {/* A button has no `layout` section and no per-instance padding by design — its inset is baked
            into the variant token (SPEC §5). Authors hunt the panel for "padding"/"spacing" and find
            nothing; this names where each actually lives instead of leaving a silent absence. */}
        <p className="pt-1 text-[11px] leading-snug text-muted-foreground">
          Padding comes from the button variant. To space this button from its neighbours, set the
          parent stack’s <span className="font-medium text-foreground">gap</span>.
        </p>
      </>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------- style

/** FROSTED GLASS, AND THE TWO THINGS IT DRAGS WITH IT (SPEC §5.0e).
 *
 *  Writing `style.backdrop` alone produces a document that does not publish: a gated block MUST carry
 *  `requires: ['backdrop_blur']` AND a fallback, because a gated block with no fallback is DROPPED on
 *  a device without the capability (§7.1) — the card simply vanishes, which is strictly worse than a
 *  flat version of it. So this writes all three, and withdraws all three.
 *
 *  It lives beside the Style section now rather than inside Motion. SPEC groups motion and backdrop
 *  together, correctly, because the interesting thing about them is which one is GATED — but that is
 *  a contract concern. An author looking for frosted glass looks under Style. */
function backdropSetter(set: Setter): (blur: number | undefined) => void {
  return (blur) =>
    set('style.backdrop', (n) => {
      const req = new Set(n.requires ?? []);
      if (blur === undefined) {
        assignStyle(n, { backdrop: undefined });
        req.delete('backdrop_blur');
        // Only OUR fallback is withdrawn — one the author wrote themselves is theirs to keep.
        if (n.fallback?.id === `${n.id}__solid`) delete n.fallback;
      } else {
        assignStyle(n, { backdrop: { blur } });
        req.add('backdrop_blur');
        if (!n.fallback) n.fallback = solidFallback(n);
      }
      n.requires = req.size > 0 ? [...req] : undefined;
    });
}

function StyleSection({ node, theme, set, issues }: SectionProps): React.JSX.Element {
  const setBackdrop = backdropSetter(set);
  return (
    <>
      <BackgroundField node={node} theme={theme} set={set} issues={issues} />
      <RadiusField node={node} set={set} />
      {/* PROGRESSIVE DISCLOSURE. Background and radius are what people reach for; opacity, border
          and shadow are not, and rendering all five as identical rows is how the panel got to ~25. */}
      <Disclosure>
        <Field
          label="opacity"
          set={node.style?.opacity !== undefined}
          onUnset={() => set('style.opacity', (n) => void assignStyle(n, { opacity: undefined }))}
        >
          {/* BOUNDED, so a slider is genuinely right — and it hands the walker `role="slider"` with
              the schema's own min/max, which a number box could only promise. */}
          <Slider
            label="opacity"
            value={node.style?.opacity}
            fallback={1}
            min={0}
            max={1}
            step={0.05}
            onChange={(opacity) => set('style.opacity', (n) => void assignStyle(n, { opacity }))}
          />
        </Field>

        <Field
          label="border"
          error={issues['style.border.color']}
          set={node.style?.border !== undefined}
          onUnset={() => set('style.border', (n) => void assignStyle(n, { border: undefined }))}
        >
          <BorderEditor node={node} theme={theme} set={set} />
        </Field>

        <Field
          label="shadow"
          error={issues['style.shadow.color']}
          set={node.style?.shadow !== undefined}
          onUnset={() => set('style.shadow', (n) => void assignStyle(n, { shadow: undefined }))}
        >
          <ShadowEditor node={node} theme={theme} set={set} />
        </Field>

        {/* MOVED OUT OF MOTION. Frosted glass is a style — it belongs beside the background it
            blurs, not beside entry animations. It sat under Motion because SPEC §5.0e groups the two,
            but that grouping is about which of them is capability-gated, which is a contract concern
            and not how an author looks for it. */}
        <Field
          label="backdrop blur"
          error={issues['style.backdrop']}
          set={node.style?.backdrop !== undefined}
          onUnset={() => setBackdrop(undefined)}
          note={
            node.style?.backdrop
              ? 'Gated on backdrop_blur — the fallback below keeps the card from vanishing on a device that cannot blur.'
              : undefined
          }
        >
          <NumberInput aria-label="backdrop blur" min={0} max={100} value={node.style?.backdrop?.blur} onChange={setBackdrop} />
        </Field>
      </Disclosure>
    </>
  );
}

// ---------------------------------------------------------------------------- interaction

function InteractionSection({ node, set, issues, quiet }: SectionProps & { quiet: boolean }): React.JSX.Element {
  return (
    // ANY block is tappable (SPEC §5.0), so this section runs for all 12 — it used to sit inside the
    // `button` arm, which is exactly why nothing else could be given an action.
    <Section title={TITLE.interaction} quiet={quiet}>
      <ActionsField
        actions={node.actions ?? []}
        issues={issues}
        onChange={(actions) => set('actions', (n) => void (n.actions = actions.length ? actions : undefined))}
      />
      {/* A11Y SITS IN THE SAME SECTION AS ACTIONS, ON PURPOSE. Put it in its own section — or behind
          a disclosure pill — and naming a tappable block becomes optional; here it is unavoidable,
          because you cannot make something tappable without seeing the field that names it (§5.0a).
          The B2 registry does NOT get to "tidy" this into its own section. */}
      <Field label="a11y label" error={issues['a11y_label']} set={node.a11y_label !== undefined}>
        <TextInput
          aria-label="a11y label"
          value={node.a11y_label ?? ''}
          placeholder={node.actions?.length ? 'what a screen reader says' : 'derived from content'}
          onChange={(v) => set('a11y_label', (n) => void (n.a11y_label = v || undefined))}
        />
      </Field>
      <Field label="decorative" set={node.a11y_hidden === true}>
        <BoolInput
          label="hide from screen readers"
          value={node.a11y_hidden ?? false}
          onChange={(v) => set('a11y_hidden', (n) => void (n.a11y_hidden = v || undefined))}
        />
      </Field>
      {/* A NAG, NOT A BLOCK. Refusing to save a draft over a missing label is hostile; shipping an
          unlabelled tap target is worse. So: warn, loudly, and let them publish. */}
      {node.actions?.length && !node.a11y_label && !hasOwnText(node) ? (
        <p className="px-1 text-[11px] text-amber-500">
          Tappable with no label — a screen reader will announce an unnamed button.
        </p>
      ) : null}
    </Section>
  );
}

// ---------------------------------------------------------------------------- compatibility

function CompatSection({ node, set, quiet }: SectionProps & { quiet: boolean }): React.JSX.Element {
  return (
    <Section title={TITLE.compat} quiet={quiet}>
      {/* A capability that a STYLE FIELD implies cannot be hand-removed. `backdrop` without
          `backdrop_blur` is a card that renders with NO BACKGROUND over the host app (SPEC §5.0e) —
          and the free-text editor would happily let you type it away. The gate belongs to the field;
          the author owns everything else in this list. */}
      <Disclosure>
        <RequiresField
          requires={node.requires}
          set={(node.requires?.length ?? 0) > 0}
          onChange={(requires) =>
            set('requires', (n) => {
              const next = new Set(requires ?? []);
              if (n.style?.backdrop) next.add('backdrop_blur');
              if (next.size > 0) n.requires = [...next];
              else delete n.requires;
            })
          }
        />
      </Disclosure>
      {/* An unregistered `custom` identifier, or an unsatisfied `requires`, renders the block's
          fallback — and NOTHING at all if it has none. */}
      {(node.requires?.length || node.block === 'custom') && !node.fallback ? (
        <p className="text-[11px] text-amber-500">
          No fallback: on a device that cannot render this block, nothing appears here.
        </p>
      ) : null}
    </Section>
  );
}
const ACTION_TYPES = [
  'dismiss',
  'open_url',
  'deep_link',
  'track_event',
  'flow_next',
  'flow_back',
  'launch_experience',
  'host_callback',
] as const;

/** Data-only, tagged on `type` — no code, no expressions, ever. `flow_next`/`flow_back` are how a
 *  button walks the steps; the WIRE flow model that honors them lands in P4. */
function ActionsField({
  actions,
  issues,
  onChange,
}: {
  actions: Action[];
  issues: FieldIssues;
  onChange: (actions: Action[]) => void;
}): React.JSX.Element {
  const replace = (index: number, action: Action): void =>
    onChange(actions.map((a, i) => (i === index ? action : a)));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">actions</span>
        <button
          type="button"
          className="rounded border px-1.5 py-0.5 text-[11px] hover:bg-secondary disabled:opacity-30"
          disabled={actions.length >= 4}
          onClick={() => onChange([...actions, { type: 'dismiss' }])}
        >
          + action
        </button>
      </div>

      {actions.map((action, index) => (
        <div key={index} className="space-y-1 rounded-md border p-1.5">
          <div className="flex items-center gap-1">
            <SelectInput
              className="flex-1"
              aria-label={`action ${index} type`}
              value={action.type}
              options={ACTION_TYPES}
              onChange={(type) => replace(index, blankAction(type))}
            />
            <button
              type="button"
              aria-label={`Remove action ${index}`}
              className="p-1 text-muted-foreground hover:text-destructive"
              onClick={() => onChange(actions.filter((_, i) => i !== index))}
            >
              <X className="size-3" />
            </button>
          </div>

          {action.type === 'open_url' || action.type === 'deep_link' ? (
            <Field label="url" error={issues[`actions.${index}.url`]}>
              <TextInput
                aria-label={`action ${index} url`}
                value={action.url}
                onChange={(url) => replace(index, { ...action, url })}
              />
            </Field>
          ) : null}
          {action.type === 'track_event' ? (
            <Field label="event" error={issues[`actions.${index}.name`]}>
              <TextInput
                aria-label={`action ${index} event`}
                value={action.name}
                onChange={(name) => replace(index, { ...action, name })}
              />
            </Field>
          ) : null}
          {action.type === 'launch_experience' ? (
            // No sane default exists — a placeholder campaign id would publish and silently launch
            // nothing. Ship the empty field and let the law show up under it.
            <Field label="campaign" error={issues[`actions.${index}.campaign_id`]}>
              <TextInput
                aria-label={`action ${index} campaign`}
                value={action.campaign_id}
                onChange={(campaign_id) => replace(index, { ...action, campaign_id })}
              />
            </Field>
          ) : null}
          {action.type === 'host_callback' ? (
            // The escape hatch: the host app registered a delegate under this name, so a button can
            // do anything the customer's app can do — with no SDK release and no schema change.
            <Field label="handler" error={issues[`actions.${index}.name`]}>
              <TextInput
                aria-label={`action ${index} handler`}
                value={action.name}
                onChange={(name) => replace(index, { ...action, name })}
              />
            </Field>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Placeholders where one can be honest (a URL, an event name — both are strings the author will
 *  overwrite, and both are publishable as-is). NOT for `launch_experience`: an invented campaign id
 *  would pass the schema and then launch nothing on device. That one starts empty and shows its law. */
function blankAction(type: (typeof ACTION_TYPES)[number]): Action {
  switch (type) {
    case 'open_url':
      return { type, url: 'https://example.com' };
    case 'deep_link':
      return { type, url: 'myapp://home' };
    case 'track_event':
      return { type, name: 'button_tapped' };
    // The name is the CUSTOMER's — whatever their app registered a delegate under. A placeholder is
    // honest here in a way an invented campaign id is not: an unregistered name is a documented
    // no-op (SPEC §9), not a silent failure.
    case 'host_callback':
      return { type, name: 'open_paywall' };
    case 'launch_experience':
      return { type, campaign_id: '' };
    default:
      return { type };
  }
}

/** Style is one optional object; a `radius` set on a block with no style must not throw. An
 *  all-undefined style is deleted, so nothing empty ends up on the wire. */
/** ENTRY / STAGGER / PARALLAX, plus the one gated style field (SPEC §5.0e).
 *
 *  `backdrop` lives here rather than under Style because it is the same question — how does this
 *  card FEEL — and because it is the only style field that drags a capability gate behind it. The
 *  gate is not optional: the inspector wires `requires[]` for you, because a `backdrop` without it
 *  is a card that renders with no background at all on every device that cannot blur. */
function MotionField({
  node,
  set,
  issues,
  inScroll,
  quiet,
}: {
  node: BlockNode;
  set: Setter;
  issues: FieldIssues;
  inScroll: boolean;
  quiet: boolean;
}): React.JSX.Element {
  const a = node.style?.animate;
  const entry = a?.entry;
  const isContainer = 'children' in node;

  /** THE GATE AND THE FALLBACK BOTH TRAVEL WITH THE FIELD.
   *
   *  An author cannot be expected to know that `backdrop` needs a capability string AND a fallback
   *  subtree — and a document missing either does not publish (SPEC §5.0e). So the inspector writes
   *  the degradation: the fallback IS this card, minus the blur, with its ids suffixed. That is
   *  exactly what the spec asks for ("the same card with a solid bg"), and it means the author gets
   *  a correct document by moving one slider. */
  // Motion is optional, so the section collapses when unused: an empty open section with only a
  // bottom chevron looks broken and gives no hint about what it reveals. This section had that
  // behaviour first, locally; `isQuiet` is that idea generalised, so the decision now lives in one
  // place for all nine sections instead of being re-derived per component.
  return (
    <Section
      title={TITLE.motion}
      hasIssue={Object.keys(issues).some((k) => k.startsWith('style.'))}
      quiet={quiet}
    >
      <Field label="entry" set={entry !== undefined}>
        <SelectInput
          aria-label="entry"
          value={entry?.kind ?? 'none'}
          options={['none', 'fade', 'fade_up', 'scale_in'] as const}
          onChange={(kind) =>
            set('style.animate', (n) =>
              assignStyle(n, {
                animate: cleanAnimate({
                  ...a,
                  entry: kind === 'none' ? undefined : { kind, duration_ms: entry?.duration_ms ?? 400, ...(entry?.delay_ms !== undefined ? { delay_ms: entry.delay_ms } : {}) },
                }),
              })
            )
          }
        />
      </Field>
      {entry ? (
        <Field label="duration / delay">
          <span className="flex items-center gap-1">
            <NumberInput
              className="w-[56px]"
              aria-label="entry duration"
              min={50}
              max={5000}
              value={entry.duration_ms}
              onChange={(v) =>
                set('style.animate', (n) =>
                  assignStyle(n, { animate: cleanAnimate({ ...a, entry: { ...entry, duration_ms: v ?? 400 } }) })
                )
              }
            />
            <NumberInput
              className="w-[56px]"
              aria-label="entry delay"
              min={0}
              max={5000}
              value={entry.delay_ms}
              onChange={(v) =>
                set('style.animate', (n) =>
                  assignStyle(n, { animate: cleanAnimate({ ...a, entry: { ...entry, delay_ms: v } }) })
                )
              }
            />
          </span>
        </Field>
      ) : null}

      {/* STAGGER BELONGS TO THE CONTAINER, so it is only offered on one — a strip that cascades is
          ONE field on the strip, and it stays right when a card is added. Publish rejects it
          anywhere else. */}
      {isContainer ? (
        <Field
          label="stagger"
          error={issues['style.animate.stagger_ms']}
          set={a?.stagger_ms !== undefined}
          onUnset={() =>
            set('style.animate', (n) => assignStyle(n, { animate: cleanAnimate({ ...a, stagger_ms: undefined }) }))
          }
        >
          <NumberInput
            aria-label="stagger"
            min={0}
            max={1000}
            value={a?.stagger_ms}
            onChange={(v) =>
              set('style.animate', (n) =>
                assignStyle(n, { animate: cleanAnimate({ ...a, stagger_ms: v }) })
              )
            }
          />
        </Field>
      ) : null}

      {/* PARALLAX IS SCROLL-DRIVEN, so it is only offered when there IS a scroll above: it
          translates against that scroll's offset, and without one it would never move. Publish
          rejects it — an inspector that offers it anyway is an inspector that lies. */}
      {inScroll ? (
        <Field
          label="parallax"
          error={issues['style.animate.parallax']}
          set={a?.parallax !== undefined}
          onUnset={() =>
            set('style.animate', (n) => assignStyle(n, { animate: cleanAnimate({ ...a, parallax: undefined }) }))
          }
        >
          {/* Centre-detented at 0 by construction: −1…1 with 0 in the middle, so "no parallax" is
              the resting position of the control rather than a number you have to know to type. */}
          <Slider
            label="parallax"
            value={a?.parallax}
            fallback={0}
            min={-1}
            max={1}
            step={0.05}
            onChange={(v) =>
              set('style.animate', (n) =>
                assignStyle(n, { animate: cleanAnimate({ ...a, parallax: v }) })
              )
            }
          />
        </Field>
      ) : null}

    </Section>
  );
}


/** THE DEGRADATION, WRITTEN FOR YOU: this card without the blur. A gated block with no fallback is
 *  DROPPED when the capability is missing (§7.1) — the card would simply vanish on a device that
 *  cannot blur, which is strictly worse than the flat version of it.
 *
 *  Ids are suffixed because they must stay unique within the document: the snapshot is keyed by id,
 *  and a duplicate would make two nodes fight over one rect. */
function solidFallback(node: BlockNode): BlockNode {
  // A JSON round-trip, NOT structuredClone: `node` is an immer DRAFT here, and a Proxy cannot be
  // structured-cloned (DATA_CLONE_ERR). The IR is plain JSON by construction — that is the whole
  // point of a no-code, no-eval wire format — so this is both safe and exact.
  const clone = JSON.parse(JSON.stringify(node)) as BlockNode;
  const rename = (n: BlockNode): void => {
    n.id = `${n.id}__solid`;
    if ('children' in n) n.children.forEach(rename);
  };
  rename(clone);
  delete clone.requires;
  delete clone.fallback;
  if (clone.style) {
    const style = { ...clone.style };
    delete style.backdrop;
    clone.style = style;
  }
  // The id the setter looks for when the author turns the blur back off.
  clone.id = `${node.id}__solid`;
  return clone;
}

/** An `animate` with every member cleared is not an empty object on the wire — it is ABSENT. */
function cleanAnimate(a: NonNullable<BlockNode['style']>['animate']): NonNullable<BlockNode['style']>['animate'] {
  if (!a) return undefined;
  const next = { ...a };
  for (const [k, v] of Object.entries(next)) {
    if (v === undefined) delete (next as Record<string, unknown>)[k];
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/** `bg` is a colour token OR a linear gradient (SPEC §5.0c). Gradient STOPS are tokens too — that
 *  is what keeps dark mode alive on the biggest colour surface in the card, so they are token
 *  pickers, not colour wells. */
function BackgroundField({
  node,
  theme,
  set,
  issues,
}: {
  node: BlockNode;
  theme: Theme;
  set: Setter;
  issues: FieldIssues;
}): React.JSX.Element {
  const bg = node.style?.bg;
  // Only an INLINE gradient is edited stop-by-stop (it has `stops`); a `{ gradient }` theme-token
  // reference (ADR 0014) is a token like any other and shows as one.
  const gradient = bg && typeof bg === 'object' && 'stops' in bg ? bg : undefined;
  const gradientToken = bg && typeof bg === 'object' && 'gradient' in bg ? bg.gradient : undefined;
  // The token form is a GRADIENT — reading it as "solid" lit the wrong kind, and clicking the
  // (wrongly inactive) gradient toggle from there invented inline stops over the author's token.
  const kind: BgKind = gradient || gradientToken ? 'gradient' : bg === undefined ? 'none' : 'solid';

  // The live strip. `stops` are TOKENS and evenly distributed (SPEC §5.0c — there are no per-stop
  // offsets), so the preview is exactly `linear-gradient(angle, ...light halves)`. Seeing the thing
  // is the entire point: an angle and a list of token names is not a gradient, it is a puzzle.
  const stripOf = (g: Gradient | undefined): string | undefined =>
    g ? `linear-gradient(${g.angle}deg, ${g.stops.map((t) => theme.colors[t]?.light ?? 'transparent').join(', ')})` : undefined;
  const strip = stripOf(gradient) ?? stripOf(gradientToken ? theme.gradients[gradientToken] : undefined);

  const setKind = (next: BgKind): void =>
    set('style.bg', (n) => {
      if (next === 'none') return void assignStyle(n, { bg: undefined });
      if (next === 'solid') {
        // From a gradient — inline OR token — the first stop is the honest seed: it is a colour the
        // author already chose, where `firstKey` would be an arbitrary one.
        const fromGradient = gradient?.stops[0] ?? (gradientToken ? theme.gradients[gradientToken]?.stops[0] : undefined);
        return void assignStyle(n, { bg: fromGradient ?? (typeof bg === 'string' ? bg : firstKey(theme.colors)) });
      }
      const seed = typeof bg === 'string' ? bg : firstKey(theme.colors);
      assignStyle(n, { bg: { kind: 'linear', angle: 180, stops: [seed, seed] } });
    });

  return (
    <Field
      label="background"
      error={issues['style.bg']}
      set={bg !== undefined}
      onUnset={() => set('style.bg', (n) => void assignStyle(n, { bg: undefined }))}
    >
      {/* ONE ROW, AND THE TRIGGER IS A PREVIEW — the same shape `BorderEditor` and `ShadowEditor`
          already use. This was two rows: a `background` select that chose only the KIND, and a
          separate `color` row holding the value. An author set "solid" and then had to find the
          colour under a different label; the control never showed what the background WAS. */}
      <Popover
        label="background"
        className="w-full"
        trigger={
          <>
            <BgSwatch bg={bg} theme={theme} strip={strip} />
            <span className={cn('truncate text-[12px]', bg === undefined && 'text-muted-foreground')}>
              {gradient ? 'gradient' : (gradientToken ?? (typeof bg === 'string' ? bg : 'add background'))}
            </span>
          </>
        }
      >
        <div className="space-y-2">
          <ToggleGroup label="background kind" value={kind} options={BG_KINDS} fill onChange={setKind} />

          {gradient ? (
            <>
              <div className="h-6 rounded-md border" style={{ background: strip }} />
              <Field label="angle">
                <AngleDial
                  label="angle"
                  value={gradient.angle}
                  onChange={(angle) => set('style.bg', (n) => assignStyle(n, { bg: { ...gradient, angle } }))}
                />
              </Field>
              {gradient.stops.map((stop, i) => (
                <Field key={i} label={`stop ${i + 1}`} error={issues[`style.bg.stops.${i}`]}>
                  <span className="flex min-w-0 flex-1 items-center gap-1">
                    <TokenPicker
                      label={`stop ${i + 1}`}
                      kind="colors"
                      theme={theme}
                      value={stop}
                      onChange={(c) =>
                        c &&
                        set('style.bg', (n) => {
                          const stops = [...gradient.stops];
                          stops[i] = c;
                          assignStyle(n, { bg: { ...gradient, stops } });
                        })
                      }
                      onMint={(light, dark) =>
                        set('style.bg', (n, doc) => {
                          const stops = [...gradient.stops];
                          stops[i] = mintColorToken(doc, light, dark);
                          assignStyle(n, { bg: { ...gradient, stops } });
                        })
                      }
                    />
                    {/* The schema floor is 2 stops — a one-stop gradient is a solid colour with extra
                        steps, and the author has a Solid option for that. */}
                    {gradient.stops.length > 2 ? (
                      <button
                        type="button"
                        aria-label={`Remove stop ${i + 1}`}
                        className="shrink-0 text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          set('style.bg', (n) =>
                            assignStyle(n, {
                              bg: { ...gradient, stops: gradient.stops.filter((_, j) => j !== i) },
                            })
                          )
                        }
                      >
                        <X className="size-3" />
                      </button>
                    ) : null}
                  </span>
                </Field>
              ))}
              {gradient.stops.length < 8 ? (
                <button
                  type="button"
                  className="text-left text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={() =>
                    set('style.bg', (n) =>
                      assignStyle(n, {
                        bg: { ...gradient, stops: [...gradient.stops, gradient.stops[gradient.stops.length - 1]!] },
                      })
                    )
                  }
                >
                  + stop
                </button>
              ) : null}
            </>
          ) : gradientToken ? (
            // THE TOKEN FORM (ADR 0014). Its stops are edited in the Theme studio — editing them
            // here would silently restyle every block that names the token. Detaching is the
            // explicit step that copies the stops onto this node first.
            <>
              <div className="h-6 rounded-md border" style={{ background: strip }} />
              <button
                type="button"
                className="text-left text-[11px] text-muted-foreground hover:text-foreground"
                onClick={() =>
                  set('style.bg', (n) => {
                    const g = theme.gradients[gradientToken];
                    assignStyle(n, {
                      bg: g
                        ? { kind: 'linear', angle: g.angle, stops: [...g.stops] }
                        : // A dangling token has no stops to copy; fall back to the inline seed.
                          { kind: 'linear', angle: 180, stops: [firstKey(theme.colors), firstKey(theme.colors)] },
                    });
                  })
                }
              >
                Detach {gradientToken} to edit stops
              </button>
            </>
          ) : (
            // NOT `kind === 'solid'`: opening the background of an UNSET node must offer colours,
            // not an empty popover. Picking one is also how you leave `none` — so gating the grid on
            // the kind already being solid would have made the kind toggle the only way in.
            <>
              {/* The swatch grid and the mint form CONTENTS, not the popovers that usually host them
                  — we are already inside a popover, and Radix dismisses one layer when another opens
                  (the inner content is portalled to the body, so the outer reads it as an outside
                  interaction). Nesting them made every swatch unreachable, invisibly to a click and
                  provably to the inventory walker. */}
              <TokenSwatchGrid
                label="background color"
                kind="colors"
                theme={theme}
                optional
                value={typeof bg === 'string' ? bg : undefined}
                onChange={(c) => set('style.bg', (n) => void assignStyle(n, { bg: c }))}
              />
              <ColorMintForm
                theme={theme}
                onMint={(light, dark) =>
                  set('style.bg', (n, doc) => void assignStyle(n, { bg: mintColorToken(doc, light, dark) }))
                }
                onDone={() => {}}
              />
            </>
          )}
        </div>
      </Popover>
    </Field>
  );
}

type BgKind = 'none' | 'solid' | 'gradient';

const BG_KINDS = [
  { value: 'none', icon: Ban, title: 'none' },
  { value: 'solid', icon: Square, title: 'solid colour' },
  { value: 'gradient', icon: Blend, title: 'linear gradient' },
] as const;

/** WHAT THE BACKGROUND IS, at a glance. A colour token shows BOTH halves of its pair, because that is
 *  what the token actually is (SPEC §4); a gradient shows its own strip; a `{ gradient }` token shows
 *  the strip the token resolves to, rather than reading as absent. */
function BgSwatch({
  bg,
  theme,
  strip,
}: {
  bg: Style['bg'];
  theme: Theme;
  strip: string | undefined;
}): React.JSX.Element {
  if (strip) return <span className="size-4 shrink-0 rounded-sm border" style={{ background: strip }} />;
  if (typeof bg === 'string') return <ThemeTokenSample family="colors" token={bg} theme={theme} compact />;
  return <span className="size-4 shrink-0 rounded-sm border border-dashed" />;
}

/** One radius, or four (SPEC §5.0c). The corners are PHYSICAL — a rounded card does not mirror in
 *  RTL — which is why they are labelled by position and not by `start`/`end`. */
const CORNERS = ['tl', 'tr', 'br', 'bl'] as const;

/** Plasmic's corner picker, and it maps one-to-one onto A7's `Corners {tl,tr,br,bl}`: SELECT the
 *  corners you mean, then type ONE number that edits exactly those. An empty selection means all
 *  four — which is also how the uniform `radius: number` form is authored, so the two shapes of the
 *  field stop being a mode the author has to choose and become a consequence of what they selected.
 *
 *  Four loose number boxes (what I shipped in A7) made you do that arithmetic yourself. */
type Corner = (typeof CORNERS)[number];

function RadiusField({ node, set }: { node: BlockNode; set: Setter }): React.JSX.Element {
  const r = node.style?.radius;
  const corners = typeof r === 'object' ? r : undefined;
  // A SET FIELD MUST NEVER RENDER AS UNSET. `style.radius` may be a `radii` TOKEN (a string, ADR
  // 0014) and this editor understands only the literal forms — so a token-valued radius used to
  // render a BLANK box under a lit set-dot. Same bug as a token-valued shadow reading "none".
  const token = typeof r === 'string' ? r : undefined;
  const [picked, setPicked] = useState<readonly Corner[]>([]);

  const valueAt = (k: Corner): number | undefined =>
    corners ? corners[k] : typeof r === 'number' ? r : undefined;

  // What the single number box shows: the selected corners' value when they agree, else blank —
  // the same "mixed" convention every design tool uses for a multi-selection. UNSET reads as 0,
  // not blank: 0 is what square corners actually resolve to, and an empty box reads as broken.
  const values = (picked.length ? picked : CORNERS).map(valueAt);
  const agree = values.every((v) => v === values[0]);
  const shown = agree ? (values[0] ?? (r === undefined ? 0 : undefined)) : undefined;

  const write = (value: number | undefined): void =>
    set('style.radius', (n) => {
      // Nothing selected → the UNIFORM form. Fewer bytes on the wire, and it is what the author
      // means when they say "round the corners".
      if (!picked.length) return void assignStyle(n, { radius: value });

      const next = { tl: valueAt('tl') ?? 0, tr: valueAt('tr') ?? 0, br: valueAt('br') ?? 0, bl: valueAt('bl') ?? 0 };
      for (const k of picked) next[k] = value ?? 0;
      // Four equal corners ARE a uniform radius. Collapsing keeps one shape of the field on the
      // wire per meaning, rather than two that render identically.
      assignStyle(n, { radius: CORNERS.every((k) => next[k] === next.tl) ? next.tl : next });
    });

  return (
    <Field
      label="radius"
      set={r !== undefined}
      onUnset={() => set('style.radius', (n) => void assignStyle(n, { radius: undefined }))}
    >
      <div role="group" aria-label="corners" className="grid size-7 shrink-0 grid-cols-2 gap-px">
        {CORNERS.map((k) => {
          const on = picked.includes(k);
          return (
            <button
              key={k}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={`corner ${k}`}
              title={k}
              onClick={() => setPicked(on ? picked.filter((p) => p !== k) : [...picked, k])}
              className={cn(
                'rounded-[2px] border outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                // The button sits where its corner does, so the control looks like the box it rounds.
                k === 'tl' && 'rounded-tl-md',
                k === 'tr' && 'rounded-tr-md',
                k === 'br' && 'rounded-br-md',
                k === 'bl' && 'rounded-bl-md',
                on ? 'border-indigo-500 bg-indigo-500/20' : 'border-border'
              )}
            />
          );
        })}
      </div>
      {/* A `radii` token replaces the number box entirely: showing an empty spinner beside a lit
          set-dot is the panel denying a value the document plainly holds. */}
      {token ? (
        <span className="truncate text-[12px] text-primary" title={token}>
          {token}
        </span>
      ) : (
      <NumberInput
        className="w-[56px]"
        aria-label="radius"
        min={0}
        value={shown}
        onChange={write}
      />
      )}
    </Field>
  );
}

/** An UNBOUNDED extent (gap, spacing): drag the label, or type in the box. A slider needs two ends
 *  and `gap` has only one, which is why these scrub and `opacity` does not. The `<input
 *  type="number">` underneath is untouched by the drag — so the role-walker still sees a spinbutton
 *  carrying the schema's `min`. */
function Scrub({
  label,
  value,
  onChange,
  min = 0,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
}): React.JSX.Element {
  const scrub = useScrub({ value, onChange, min });
  return (
    <Field label={label} scrub={scrub} set={value !== 0}>
      {/* Lowercased: every other accessible name in the panel is lowercase, and the display label
          (capitalised for the rail) rode straight into `aria-label`, making `Gap` the one outlier
          in the inventory golden. */}
      <NumberInput aria-label={label.toLowerCase()} min={min} value={value} onChange={(n) => onChange(n ?? 0)} />
    </Field>
  );
}

/** One popover, and the trigger IS the preview — Plasmic's `shadow-thumb`. Five loose number boxes
 *  in a row told you the numbers; this tells you the shadow.
 *
 *  THE ANCHOR RULE SURVIVES THE MOVE. `y` is the member that CREATES the shadow, so clearing it
 *  clears the whole object — the schema has no notion of a shadow without one. `x` and `spread`
 *  default to absent, so a v1.0 document keeps painting exactly as it did.
 *  ponytail: the colour inside stays the native `TokenPicker` (swatch + select) rather than the
 *  grid — a popover inside a popover buys nothing here, and the select is walkable as-is. */
/** One preview for an inline shadow AND for a resolved `elevations` token — the two must look the
 *  same, because to the author they ARE the same thing wearing different clothes. */
function shadowPreview(s: Shadow | undefined, theme: Theme): React.CSSProperties | undefined {
  const colour = s ? theme.colors[s.color] : undefined;
  if (!s || !colour) return undefined;
  return { boxShadow: `${s.x ?? 0}px ${s.y}px ${s.blur}px ${s.spread ?? 0}px ${colour.light}` };
}

function ShadowEditor({ node, theme, set }: { node: BlockNode; theme: Theme; set: Setter }): React.JSX.Element {
  // Only an INLINE shadow is edited here; an `elevations` token reference (a string, ADR 0014) is
  // applied from the Theme panel, so treat it as "no inline shadow" in this per-node editor.
  const s = typeof node.style?.shadow === 'object' ? node.style.shadow : undefined;
  // A SET FIELD MUST NEVER RENDER AS UNSET. `style.shadow` may be an `elevations` TOKEN (a string,
  // ADR 0014), and this editor only understands the inline form — so a token-valued shadow used to
  // show "none" while `Field`'s set-dot stayed lit. Same bug shape as a `radii` token rendering a
  // blank box: the panel telling the author their value is not there.
  const token = typeof node.style?.shadow === 'string' ? node.style.shadow : undefined;

  const patch = (key: 'x' | 'blur' | 'spread', value: number | undefined): void =>
    set('style.shadow', (n) => {
      const cur = n.style?.shadow;
      if (!cur || typeof cur === 'string') return;
      if (key === 'blur' && value === undefined) return; // blur is required — an empty box is not a delete
      assignStyle(n, { shadow: { ...cur, [key]: value } });
    });

  return (
    <Popover
      label="shadow"
      trigger={
        <>
          <span
            className="size-4 shrink-0 rounded-sm border bg-muted"
            style={shadowPreview(s ?? (token ? theme.elevations[token] : undefined), theme)}
          />
          <span className={cn('truncate text-[12px]', !s && !token && 'text-muted-foreground', token && 'text-primary')}>
            {token ?? (s ? `${s.y}/${s.blur}` : 'add shadow')}
          </span>
        </>
      }
    >
      <div className="space-y-2">
        <Field label="y">
          <NumberInput
            aria-label="shadow y"
            value={s?.y}
            onChange={(y) =>
              set('style.shadow', (n) => {
                const cur = typeof n.style?.shadow === 'object' ? n.style.shadow : undefined;
                assignStyle(n, {
                  shadow:
                    y === undefined
                      ? undefined
                      : {
                          ...cur,
                          y,
                          blur: cur?.blur ?? 8,
                          color: cur?.color ?? firstKey(theme.colors),
                        },
                });
              })
            }
          />
        </Field>
        <Field label="x">
          <NumberInput aria-label="shadow x" value={s?.x} onChange={(x) => patch('x', x)} />
        </Field>
        <Field label="blur">
          <NumberInput aria-label="shadow blur" min={0} value={s?.blur} onChange={(v) => patch('blur', v)} />
        </Field>
        <Field label="spread">
          <NumberInput aria-label="shadow spread" min={0} value={s?.spread} onChange={(v) => patch('spread', v)} />
        </Field>
        <Field label="colour">
          <TokenPicker
            label="shadow colour"
            kind="colors"
            theme={theme}
            value={s?.color}
            onChange={(color) =>
              set('style.shadow', (n) => {
                const cur = n.style?.shadow;
                if (!cur || typeof cur === 'string' || color === undefined) return;
                assignStyle(n, { shadow: { ...cur, color } });
              })
            }
            onMint={(light, dark) =>
              set('style.shadow', (n, doc) => {
                const cur = n.style?.shadow;
                if (!cur || typeof cur === 'string') return;
                assignStyle(n, { shadow: { ...cur, color: mintColorToken(doc, light, dark) } });
              })
            }
          />
        </Field>
      </div>
    </Popover>
  );
}

/** Same shape, same anchor rule: `width` is what creates a border, so clearing it removes it. */
function BorderEditor({ node, theme, set }: { node: BlockNode; theme: Theme; set: Setter }): React.JSX.Element {
  const b = node.style?.border;
  const colour = b ? theme.colors[b.color] : undefined;

  return (
    <Popover
      label="border"
      trigger={
        <>
          <span
            className="size-4 shrink-0 rounded-sm"
            style={
              b && colour
                ? { border: `${Math.min(b.width, 4)}px solid ${colour.light}` }
                : { border: '1px dashed hsl(var(--border))' }
            }
          />
          <span className={cn('truncate text-[12px]', !b && 'text-muted-foreground')}>
            {b ? `${b.width}` : 'add border'}
          </span>
        </>
      }
    >
      <div className="space-y-2">
        <Field label="width">
          <NumberInput
            aria-label="border width"
            min={0}
            value={b?.width}
            onChange={(width) =>
              set('style.border', (n) =>
                assignStyle(n, {
                  border:
                    width === undefined
                      ? undefined
                      : { width, color: n.style?.border?.color ?? firstKey(theme.colors) },
                })
              )
            }
          />
        </Field>
        <Field label="colour">
          <TokenPicker
            label="border colour"
            kind="colors"
            theme={theme}
            value={b?.color}
            onChange={(color) =>
              set('style.border', (n) => {
                if (!n.style?.border || color === undefined) return;
                assignStyle(n, { border: { ...n.style.border, color } });
              })
            }
            onMint={(light, dark) =>
              set('style.border', (n, doc) => {
                if (!n.style?.border) return;
                assignStyle(n, { border: { ...n.style.border, color: mintColorToken(doc, light, dark) } });
              })
            }
          />
        </Field>
      </div>
    </Popover>
  );
}

function assignStyle(node: BlockNode, patch: Partial<NonNullable<BlockNode['style']>>): void {
  const next: Record<string, unknown> = { ...node.style, ...patch };
  for (const [key, value] of Object.entries(next)) if (value === undefined) delete next[key];
  if (Object.keys(next).length === 0) delete node.style;
  else node.style = next as NonNullable<BlockNode['style']>;
}

function setPadding(set: Setter, padding: ReturnType<typeof Object> | undefined): void {
  set('padding', (node) => {
    const target = node as { padding?: unknown };
    if (padding) target.padding = padding;
    else delete target.padding;
  });
}

const firstKey = (record: Record<string, unknown>): string => Object.keys(record)[0] ?? '';
